import React, { FC, useState } from "react";
import { Progress, Tooltip } from "antd";
import moment from "moment";
import {
  AiDetection,
  MediaAiAnalysis,
} from "./types";
import BeamIcon, { BeamIconName } from "./BeamIcon";
import "./MessageTypes.scss";

/**
 * AI Visual & Auditory Recognition for a single media item.
 *
 * Two halves of one job: what the model saw and what it heard. They share a
 * card because an operator asks one question — "what is in this clip?" — and
 * splitting that across two widgets would make them scan twice.
 *
 * The card is deliberately honest about uncertainty. Every detection and every
 * transcript line carries its confidence, and anything below the threshold is
 * visibly marked rather than presented as fact — these clips end up in incident
 * logs, so a 58% guess must not read like a 96% one.
 *
 * Analysis is asynchronous, so queued / analyzing / failed are first-class
 * states here, not an afterthought.
 */

interface MediaAiCardProps {
  analysis: MediaAiAnalysis;
  /** Jump the player to a timestamp (transcript line, sound event, detection). */
  onSeek?: (atSec: number, analysis: MediaAiAnalysis) => void;
  /** Re-run recognition — used by the failed state and the header action. */
  onRerun?: (analysis: MediaAiAnalysis) => void;
  /** Promote a label or keyword to a page tag so it becomes searchable. */
  onUseAsTag?: (tag: string, analysis: MediaAiAnalysis) => void;
}

const KIND_ICON: Record<MediaAiAnalysis["mediaKind"], BeamIconName> = {
  image: "image",
  video: "video",
  audio: "audio",
};

/** Below this the model is guessing, and the UI says so instead of implying fact. */
const LOW_CONFIDENCE = 0.7;

const clockTime = (seconds: number) => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
};

const confidenceClass = (value: number) =>
  value >= 0.85 ? "is-high" : value >= LOW_CONFIDENCE ? "is-mid" : "is-low";

const MediaAiCard: FC<MediaAiCardProps> = ({
  analysis,
  onSeek,
  onRerun,
  onUseAsTag,
}) => {
  const hasVisual = !!analysis.visual;
  const hasAudio = !!analysis.audio;
  const [tab, setTab] = useState<"visual" | "audio">(
    hasVisual ? "visual" : "audio"
  );
  // Hovering a detection row lights up its box on the frame, and vice versa.
  const [hovered, setHovered] = useState<string | null>(null);

  const renderHeader = () => (
    <header className="mt-card__head">
      <h4>
        <BeamIcon name={KIND_ICON[analysis.mediaKind]} size={14} /> AI
        Recognition
      </h4>

      {analysis.status === "ready" && hasVisual && hasAudio && (
        <div className="mt-segmented">
          <button
            type="button"
            className={tab === "visual" ? "is-active" : ""}
            onClick={() => setTab("visual")}
          >
            Visual
            <span className="mt-segmented__count">
              {analysis.visual?.detections.length ?? 0}
            </span>
          </button>
          <button
            type="button"
            className={tab === "audio" ? "is-active" : ""}
            onClick={() => setTab("audio")}
          >
            Audio
            <span className="mt-segmented__count">
              {analysis.audio?.transcript?.length ?? 0}
            </span>
          </button>
        </div>
      )}

      <button
        type="button"
        className="mt-btn mt-btn--link"
        onClick={() => onRerun?.(analysis)}
      >
        <BeamIcon name="refresh" size={13} /> Re-analyse
      </button>
    </header>
  );

  /* ---------------- queued / analyzing / failed ---------------- */

  const renderPending = () => {
    if (analysis.status === "failed") {
      return (
        <div className="mt-ai__state mt-ai__state--failed">
          <BeamIcon name="warning" size={22} />
          <p className="mt-ai__state-title">Recognition failed</p>
          <p className="mt-ai__state-detail">{analysis.error}</p>
          <button
            type="button"
            className="mt-btn mt-btn--ghost"
            onClick={() => onRerun?.(analysis)}
          >
            <BeamIcon name="refresh" size={13} /> Try again
          </button>
        </div>
      );
    }

    if (analysis.status === "unsupported") {
      return (
        <div className="mt-ai__state">
          <BeamIcon name="file" size={22} />
          <p className="mt-ai__state-title">Not supported</p>
          <p className="mt-ai__state-detail">
            This media type has no recognition model yet.
          </p>
        </div>
      );
    }

    // A determinate bar rather than a spinner: a spinner tells an operator
    // nothing about whether it is worth waiting.
    return (
      <div className="mt-ai__state">
        <p className="mt-ai__state-title">
          {analysis.status === "queued" ? "Queued for analysis" : "Analysing…"}
        </p>
        <Progress
          percent={analysis.progress ?? 0}
          status="active"
          strokeColor="#508afa"
          showInfo={analysis.status === "analyzing"}
        />
        <p className="mt-ai__state-detail">
          {analysis.model} · results appear here automatically
        </p>
      </div>
    );
  };

  /* ---------------- visual ---------------- */

  const renderDetection = (detection: AiDetection) => (
    <li
      key={detection.id}
      className={`mt-ai__row ${hovered === detection.id ? "is-hovered" : ""}`}
      onMouseEnter={() => setHovered(detection.id)}
      onMouseLeave={() => setHovered(null)}
    >
      <button
        type="button"
        className="mt-ai__row-main"
        onClick={() =>
          detection.atSec !== undefined && onSeek?.(detection.atSec, analysis)
        }
      >
        <span className="mt-ai__label">
          {detection.label}
          {detection.confidence < LOW_CONFIDENCE && (
            <Tooltip title="Below the confidence threshold — verify before relying on it">
              <em className="mt-ai__unsure">unverified</em>
            </Tooltip>
          )}
        </span>
        <span
          className={`mt-ai__meter ${confidenceClass(detection.confidence)}`}
        >
          <i style={{ width: `${detection.confidence * 100}%` }} />
        </span>
        <span className="mt-ai__pct mt-mono">
          {Math.round(detection.confidence * 100)}%
        </span>
        {detection.atSec !== undefined && (
          <span className="mt-ai__at mt-mono">{clockTime(detection.atSec)}</span>
        )}
      </button>
      <Tooltip title="Add as page tag">
        <button
          type="button"
          className="mt-ai__tag-btn"
          onClick={() => onUseAsTag?.(detection.label, analysis)}
        >
          <BeamIcon name="plus" size={11} />
        </button>
      </Tooltip>
    </li>
  );

  const renderVisual = () => {
    const visual = analysis.visual!;

    return (
      <div className="mt-ai__split">
        <div className="mt-ai__frame">
          {analysis.thumbnail ? (
            <img src={analysis.thumbnail} alt={analysis.mediaName} />
          ) : (
            <div className="mt-ai__frame-empty">No frame available</div>
          )}

          {/* Boxes are positioned in percentages so the overlay tracks the
              image at whatever size it renders. */}
          {visual.detections
            .filter((detection) => detection.box)
            .map((detection) => (
              <span
                key={detection.id}
                className={`mt-ai__box ${confidenceClass(
                  detection.confidence
                )} ${hovered === detection.id ? "is-hovered" : ""}`}
                style={{
                  left: `${detection.box!.x}%`,
                  top: `${detection.box!.y}%`,
                  width: `${detection.box!.w}%`,
                  height: `${detection.box!.h}%`,
                }}
                onMouseEnter={() => setHovered(detection.id)}
                onMouseLeave={() => setHovered(null)}
              >
                <b>{detection.label}</b>
              </span>
            ))}
        </div>

        <div className="mt-ai__detail">
          {visual.scene && (
            <p className="mt-ai__scene">
              <BeamIcon name="camera" size={13} /> {visual.scene}
            </p>
          )}

          <h6 className="mt-ai__subhead">Detected objects</h6>
          <ul className="mt-ai__list">
            {visual.detections.map(renderDetection)}
          </ul>

          {visual.ocr.length > 0 && (
            <>
              <h6 className="mt-ai__subhead">Text in media</h6>
              <ul className="mt-ai__ocr">
                {visual.ocr.map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
            </>
          )}

          {!!visual.flags?.length && (
            <>
              <h6 className="mt-ai__subhead">Review flags</h6>
              <div className="mt-ai__flags">
                {visual.flags.map((flag) => (
                  <span
                    key={flag.label}
                    className={`mt-ai__flag mt-ai__flag--${flag.severity}`}
                  >
                    <BeamIcon
                      name={flag.severity === "info" ? "alert" : "warning"}
                      size={11}
                    />
                    {flag.label}
                  </span>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  /* ---------------- audio ---------------- */

  const renderAudio = () => {
    const audio = analysis.audio!;

    return (
      <div className="mt-ai__audio">
        <div className="mt-ai__audio-meta">
          {audio.language && (
            <span>
              <em>Language</em> {audio.language}
            </span>
          )}
          {audio.durationSec !== undefined && (
            <span>
              <em>Duration</em> {clockTime(audio.durationSec)}
            </span>
          )}
          {audio.speakers !== undefined && (
            <span>
              <em>Speakers</em> {audio.speakers}
            </span>
          )}
        </div>

        {!!audio.keywords?.length && (
          <div className="mt-ai__chips">
            {audio.keywords.map((word) => (
              <button
                key={word}
                type="button"
                className="mt-ai__chip"
                onClick={() => onUseAsTag?.(word, analysis)}
              >
                #{word}
              </button>
            ))}
          </div>
        )}

        {!!audio.events?.length && (
          <>
            <h6 className="mt-ai__subhead">Sound events</h6>
            <div className="mt-ai__chips">
              {audio.events.map((event) => (
                <button
                  key={event.id}
                  type="button"
                  className={`mt-ai__chip mt-ai__chip--event ${confidenceClass(
                    event.confidence
                  )}`}
                  onClick={() => onSeek?.(event.atSec, analysis)}
                >
                  <BeamIcon name="audio" size={11} />
                  {event.label}
                  <span className="mt-mono">{clockTime(event.atSec)}</span>
                </button>
              ))}
            </div>
          </>
        )}

        {!!audio.transcript?.length && (
          <>
            <h6 className="mt-ai__subhead">Transcript</h6>
            <ol className="mt-ai__transcript">
              {audio.transcript.map((line) => (
                <li key={line.id}>
                  <button
                    type="button"
                    onClick={() => onSeek?.(line.atSec, analysis)}
                  >
                    <span className="mt-ai__ts mt-mono">
                      {clockTime(line.atSec)}
                    </span>
                    <span className="mt-ai__speaker">{line.speaker}</span>
                    <span
                      className={`mt-ai__said ${
                        line.confidence < LOW_CONFIDENCE ? "is-unsure" : ""
                      }`}
                    >
                      {line.text}
                    </span>
                  </button>
                </li>
              ))}
            </ol>
            <p className="mt-ai__disclaimer">
              Machine transcript — underlined lines are low confidence. Verify
              against the recording before quoting it in a report.
            </p>
          </>
        )}
      </div>
    );
  };

  return (
    <section className="mt-card mt-ai">
      {renderHeader()}

      <div className="mt-ai__source">
        <BeamIcon name={KIND_ICON[analysis.mediaKind]} size={12} />
        <span className="mt-ai__source-name">{analysis.mediaName}</span>
        {analysis.model && <span className="mt-ai__model">{analysis.model}</span>}
        {analysis.analysedAt && (
          <span className="mt-ai__when">
            {moment(analysis.analysedAt).format("D MMM YYYY, h:mm A")}
          </span>
        )}
      </div>

      <div className="mt-card__body">
        {analysis.status !== "ready"
          ? renderPending()
          : tab === "visual" && hasVisual
          ? renderVisual()
          : renderAudio()}
      </div>
    </section>
  );
};

export default MediaAiCard;
